// useImageGen — bridges the Studio composer to the server's
// imageGen.* RPCs. Splits concerns:
//   • settings (per-user saved quality/style/aspect)
//   • options (enums for the chips)
//   • generate (issues the request + opens an SSE stream for live
//     candidate progress / preview frames)
//   • gallery (recent generations)
//
// SSE: we use a per-job EventSource against /api/events?session=<id>.
// The image-gen events arrive in the same channel as agent events, so
// we filter by `toolCallId === sseSessionId` and `source === "imagegen"`.

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import {
  api,
  type GalleryItem,
  type ImageGenGenerateArgs,
  type ImageGenInpaintArgs,
  type ImageGenOptions,
  type ImageGenPrefs,
} from "./rpc";

export type CandidateState = "pending" | "running" | "done" | "error";

export interface CandidateSlot {
  idx: number;
  state: CandidateState;
  /** Progress 0..1 from sampling steps. */
  progress: number;
  /** Live preview data URL (data:image/jpeg;base64,...). Replaced as
   *  the model samples; cleared when state hits "done". */
  preview: string | null;
  galleryId: number | null;
  /** Final card. Populated when galleryId resolves and gallery.get
   *  returns. */
  item: GalleryItem | null;
  error: string | null;
}

export interface UseImageGen {
  options: ImageGenOptions | null;
  prefs: ImageGenPrefs | null;
  setPrefs: (next: ImageGenPrefs) => Promise<void>;
  gallery: GalleryItem[];
  galleryLoading: boolean;
  refreshGallery: () => Promise<void>;
  removeFromGallery: (id: number) => Promise<void>;
  // Active in-flight generation state
  jobId: string | null;
  slots: CandidateSlot[];
  generating: boolean;
  error: string | null;
  generate: (args: ImageGenGenerateArgs) => Promise<void>;
  clearCandidates: () => void;
  /** Inpaint a previously-generated image. Returns the new gallery
   *  item so callers can auto-open its detail. */
  inpaint: (args: ImageGenInpaintArgs) => Promise<GalleryItem | null>;
}

interface SseEvent {
  type: string;
  toolCallId?: string;
  source?: string;
  candidateIdx?: number;
  value?: number;
  max?: number;
  dataBase64?: string;
  mime?: string;
  galleryId?: number;
  candidateTotal?: number;
  error?: string;
}

export function useImageGen(): UseImageGen {
  const [options, setOptions] = useState<ImageGenOptions | null>(null);
  const [prefs, _setPrefs] = useState<ImageGenPrefs | null>(null);
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [slots, setSlots] = useState<CandidateSlot[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    api.imageGenListOptions().then(setOptions).catch(() => {});
    api.imageGenGetSettings().then(_setPrefs).catch(() => {});
  }, []);

  const refreshGallery = useCallback(async () => {
    setGalleryLoading(true);
    try { setGallery(await api.galleryList({ limit: 60 })); }
    catch (e: any) { setError(e?.message || "gallery list failed"); }
    finally { setGalleryLoading(false); }
  }, []);

  useEffect(() => { refreshGallery(); }, [refreshGallery]);

  const removeFromGallery = useCallback(async (id: number) => {
    try {
      await api.galleryDelete(id);
      setGallery((prev) => prev.filter((g) => g.id !== id));
    } catch (e: any) {
      setError(e?.message || "delete failed");
    }
  }, []);

  const setPrefs = useCallback(async (next: ImageGenPrefs) => {
    _setPrefs(next);
    try { await api.imageGenUpdateSettings(next); }
    catch (e: any) { setError(e?.message || "save settings failed"); }
  }, []);

  const closeStream = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
  }, []);

  // Unmount cleanup — never leak the EventSource even if the user
  // navigates away mid-generation.
  useEffect(() => () => closeStream(), [closeStream]);

  const clearCandidates = useCallback(() => {
    closeStream();
    setSlots([]);
    setJobId(null);
    setGenerating(false);
  }, [closeStream]);

  const generate = useCallback(async (args: ImageGenGenerateArgs) => {
    if (generating) return;
    closeStream();
    setError(null);
    const count = Math.max(1, Math.min(12, args.candidates || 1));
    const id = `imagegen-${Math.random().toString(36).slice(2, 10)}`;
    setJobId(id);
    setGenerating(true);
    // Seed N pending placeholders so the user sees something instantly.
    setSlots(Array.from({ length: count }, (_, i) => ({
      idx: i,
      state: "pending" as const,
      progress: 0,
      preview: null,
      galleryId: null,
      item: null,
      error: null,
    })));

    // Open SSE first so we don't miss the candidate_start events the
    // server fires as soon as generate kicks off.
    let stream: EventSource;
    try {
      stream = new EventSource(`/api/events?session=${encodeURIComponent(id)}`, { withCredentials: true });
    } catch (err: any) {
      setError(err?.message || "preview stream failed");
      return;
    }
    esRef.current = stream;
    stream.onmessage = (msg) => {
      let ev: SseEvent | null = null;
      try { ev = JSON.parse(msg.data); } catch { return; }
      if (!ev || typeof ev !== "object") return;
      // Filter: only events for our specific job.
      if (ev.toolCallId && ev.toolCallId !== id) return;
      if (typeof ev.candidateIdx !== "number") return;

      const idx = ev.candidateIdx;
      if (ev.type === "image_gen_candidate_start") {
        patchSlot(setSlots, idx, { state: "running" });
      } else if (ev.type === "image_gen_progress" && typeof ev.value === "number" && typeof ev.max === "number" && ev.max > 0) {
        patchSlot(setSlots, idx, { progress: ev.value / ev.max });
      } else if (ev.type === "image_gen_preview" && ev.dataBase64) {
        patchSlot(setSlots, idx, { preview: `data:${ev.mime || "image/jpeg"};base64,${ev.dataBase64}` });
      } else if (ev.type === "image_gen_candidate_done") {
        patchSlot(setSlots, idx, { state: "done", progress: 1, galleryId: ev.galleryId ?? null });
        if (ev.galleryId) {
          api.galleryGet(ev.galleryId).then((item) => {
            if (!item) return;
            patchSlot(setSlots, idx, { item });
            // Splice into gallery state so the "Gallery" tab reflects
            // the new row without a refresh round-trip.
            setGallery((prev) => prev.some((g) => g.id === item.id) ? prev : [item, ...prev]);
          });
        }
        // When the final candidate resolves, close the stream after a
        // short grace period — preview events can arrive a beat after
        // candidate_done depending on Comfy timing.
        if (typeof ev.candidateTotal === "number") {
          setSlots((prev) => {
            const doneCount = prev.filter((s) => s.state === "done" || s.state === "error").length;
            if (doneCount >= ev.candidateTotal!) {
              setTimeout(() => closeStream(), 1500);
              setGenerating(false);
            }
            return prev;
          });
        }
      } else if (ev.type === "image_gen_candidate_error") {
        patchSlot(setSlots, idx, { state: "error", error: ev.error || "unknown error" });
      }
    };
    stream.onerror = () => {
      // EventSource auto-reconnects on transient errors; only log here.
      // Permanent failure surfaces via the RPC return below.
    };

    try {
      await api.imageGenGenerate({ ...args, sseSessionId: id });
      // If SSE already closed the job, leave state as-is. Otherwise the
      // SSE close timer above will eventually flip generating off.
    } catch (e: any) {
      setError(e?.message || "generate failed");
      setGenerating(false);
      closeStream();
    }
  }, [generating, closeStream]);

  const inpaint = useCallback(async (args: ImageGenInpaintArgs): Promise<GalleryItem | null> => {
    setError(null);
    try {
      const r = await api.imageGenInpaint(args);
      if (r.galleryId) {
        const item = await api.galleryGet(r.galleryId);
        if (item) setGallery((prev) => prev.some((g) => g.id === item.id) ? prev : [item, ...prev]);
        return item;
      }
      // Server returned ok without a row (older code path). Refresh as a fallback.
      await refreshGallery();
      return null;
    } catch (e: any) {
      setError(e?.message || "inpaint failed");
      return null;
    }
  }, [refreshGallery]);

  return {
    options, prefs, setPrefs,
    gallery, galleryLoading, refreshGallery, removeFromGallery,
    jobId, slots, generating, error,
    generate, clearCandidates, inpaint,
  };
}

function patchSlot(
  setSlots: (fn: (prev: CandidateSlot[]) => CandidateSlot[]) => void,
  idx: number,
  patch: Partial<CandidateSlot>,
) {
  setSlots((prev) => prev.map((s) => (s.idx === idx ? { ...s, ...patch } : s)));
}
